import { Application, Point } from 'pixi.js';
import { GameManager } from '@core/GameManager';
import { HUD } from '@ui/HUD';
import { RoomQualityOverlay } from '@ui/RoomQualityOverlay';
import { RoomSelectionTool } from '@ui/RoomSelectionTool';
import { RoomLegend } from '@ui/RoomLegend';
import { RoomUICallbacks } from '@ui/RoomUI';
import { FurnitureUICallbacks } from '@ui/FurnitureUI';
import { RoomCategory } from '@gameplay/management/rooms/RoomTypes';

async function bootstrap(): Promise<void> {
  const app = new Application({
    width: 1280,
    height: 720,
    backgroundColor: 0x2c3e50,
    antialias: true
  });

  const mountNode = document.getElementById('app') ?? document.body;
  mountNode.appendChild(app.view as HTMLCanvasElement);

  const gameManager = new GameManager(app);
  await gameManager.initialize();

  // Create room UI callbacks
  const roomUICallbacks: RoomUICallbacks = {
    onRoomCategoryChanged: (roomId: string, category: RoomCategory) => {
      gameManager.setRoomCategory(roomId, category);
    },
    onServiceAnchorPlaced: (category: RoomCategory, position: Point) => {
      // TODO: Implement service anchor placement
      console.log(`Service anchor placed: ${category} at (${position.x}, ${position.y})`);
    },
    onRoomSelected: (roomId: string | null) => {
      console.log(`Room selected: ${roomId}`);
    },
    onEnterCategorizationMode: (category: RoomCategory) => {
      gameManager.enterRoomCategorizationMode(category);
    },
    onExitCategorizationMode: () => {
      gameManager.exitRoomCategorizationMode();
    }
  };

  // Create furniture UI callbacks
  const furnitureUICallbacks: FurnitureUICallbacks = {
    onFurnitureSelected: (furnitureId: string) => {
      gameManager.selectFurniture(furnitureId);
      gameManager.setPlacementMode('furniture');
    }
  };

  // Initialize HUD with room and furniture UI
  const hud = new HUD(roomUICallbacks, furnitureUICallbacks, app);
  gameManager.getUIContainer().addChild(hud);

  // Set up furniture definitions in the furniture UI
  const furnitureDefinitions = gameManager.getFurnitureSystem().getAllFurniture();
  hud.getFurnitureUI().setFurnitureDefinitions(furnitureDefinitions);

  // Create room quality overlay
  const qualityOverlay = new RoomQualityOverlay(gameManager.getGridSystem());
  gameManager.getWorldContainer().addChild(qualityOverlay);

  // Create room legend
  const roomLegend = new RoomLegend();
  roomLegend.positionAt(20, 200);
  gameManager.getUIContainer().addChild(roomLegend);

  // Create room selection tool
  const selectionTool = new RoomSelectionTool(
    gameManager.getGridSystem(),
    gameManager.getRoomManager(),
    {
      onRoomSelectionChanged: (rooms) => {
        console.log(`Selected ${rooms.length} rooms`);
        if (rooms.length === 1) {
          hud.updateSelectedRoom(rooms[0]);
        } else {
          hud.updateSelectedRoom(null);
        }
      },
      onRoomCategoryApplied: (roomIds, category) => {
        roomIds.forEach(roomId => gameManager.setRoomCategory(roomId, category));
      }
    }
  );
  gameManager.getWorldContainer().addChild(selectionTool);
  // Disable selection tool by default to avoid intercepting drags/overlay during categorization
  selectionTool.disable();

  // Set up event listeners
  hud.setPlacementMode(gameManager.getPlacementMode());
  gameManager.onPlacement((result) => {
    hud.updatePlacement(result);
  });
  gameManager.onPlacementModeChange((mode) => hud.setPlacementMode(mode));
  gameManager.onRoomUpdated((room) => {
    hud.showRoomQuality(room);
    if (room) {
      qualityOverlay.updateRoom(room);
    }
  });
  gameManager.onRemoval((mode, context, count) => hud.showRemoval(mode, context, count));

  // Set up wall mode toggle callback
  hud.setWallModeToggleCallback(() => {
    return gameManager.toggleWallBuildMode();
  });

  // Add keyboard shortcuts for UI panels
  window.addEventListener('keydown', (event) => {
    switch (event.key) {
      case 'f':
      case 'F':
        hud.toggleFurnitureUI();
        event.preventDefault();
        break;
      case 'q':
      case 'Q':
        qualityOverlay.toggleOverlay();
        event.preventDefault();
        break;
      case 'p':
      case 'P':
        // Spawn a new resident
        gameManager.spawnRandomResidents(1);
        console.log('🧓 New resident spawned!');
        event.preventDefault();
        break;
      case 'Escape':
        // Exit room categorization mode
        gameManager.exitRoomCategorizationMode();
        hud.exitRoomCategorizationMode();
        event.preventDefault();
        break;
    }
  });

  gameManager.start();

  // Spawn some initial residents for testing
  setTimeout(() => {
    gameManager.spawnRandomResidents(3);
  }, 1000);

  window.addEventListener('resize', () => {
    const width = window.innerWidth;
    const height = window.innerHeight;
    app.renderer.resize(width, height);
    const canvas = app.view as HTMLCanvasElement;
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;
  });
}

bootstrap().catch((error) => {
  console.error('Failed to bootstrap Golden Years Manor', error);
});
